import React, { useMemo, useState } from "react";
import { blockReason, rupees } from "./packRules";

const TABS = [
  { key: "A-la-carte", label: "A-la-carte channels" },
  { key: "Broadcaster", label: "Broadcaster packs" },
];

/**
 * A-la-carte / Broadcaster add-ons with search. An add-on whose channels are already in the
 * DPO / FTA or in another chosen add-on can't be ticked (the reason is shown under it).
 *
 * Props: addons { "A-la-carte": [...], "Broadcaster": [...] }, selectedIds (array, in order chosen),
 *        onChange(ids), basePacks [dpo, fta], conflicts Map(id -> reason), months, disabled
 */
const AddonPicker = ({ addons, selectedIds, onChange, basePacks, conflicts, months = 1, disabled }) => {
  const [tab, setTab] = useState("A-la-carte");
  const [q, setQ] = useState("");

  const all = useMemo(() => [...(addons["A-la-carte"] || []), ...(addons.Broadcaster || [])], [addons]);
  const byId = useMemo(() => new Map(all.map((a) => [a.id, a])), [all]);
  const selected = selectedIds.map((id) => byId.get(id)).filter(Boolean);

  const list = addons[tab] || [];
  const shown = useMemo(() => {
    const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) return list;
    return list.filter((p) => {
      const hay = [p.name, ...(p.channels || [])].join(" ").toLowerCase();
      return words.every((w) => hay.includes(w));
    });
  }, [list, q]);

  const toggle = (id) => {
    if (disabled) return;
    onChange(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);
  };

  return (
    <div className="piptv-addons">
      <div className="piptv-tabs" role="tablist">
        {TABS.map((t) => {
          const count = (addons[t.key] || []).filter((p) => selectedIds.includes(p.id)).length;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              className={`piptv-tab ${tab === t.key ? "is-active" : ""}`}
              onClick={() => { setTab(t.key); setQ(""); }}
            >
              {t.label}
              <span className="piptv-tab__count">{count}</span>
            </button>
          );
        })}
      </div>

      <div className="piptv-search">
        <span aria-hidden="true">⌕</span>
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={tab === "Broadcaster" ? "Search broadcaster packs or channels…" : "Search channels…"}
          disabled={disabled}
        />
        {q ? <button type="button" className="piptv-search__clear" onClick={() => setQ("")} aria-label="Clear search">×</button> : null}
      </div>

      <div className="piptv-list">
        {list.length === 0 ? (
          <div className="piptv-empty">No {tab === "Broadcaster" ? "broadcaster packs" : "a-la-carte channels"} are available.</div>
        ) : shown.length === 0 ? (
          <div className="piptv-empty">Nothing matches “{q.trim()}”.</div>
        ) : (
          shown.map((p) => {
            const on = selectedIds.includes(p.id);
            const clash = on ? conflicts.get(p.id) : null;
            const blocked = !on ? blockReason(p, basePacks, selected) : null;
            return (
              <label
                key={p.id}
                className={`piptv-row ${on ? "is-on" : ""} ${blocked ? "is-blocked" : ""} ${clash ? "is-clash" : ""}`}
                title={p.channels && p.channels.length > 1 ? p.channels.join(", ") : undefined}
              >
                <input type="checkbox" checked={on} disabled={disabled || !!blocked} onChange={() => toggle(p.id)} />
                <span className="piptv-row__main">
                  <span className="piptv-row__name">{p.name}</span>
                  {p.type === "Broadcaster" && p.channel_count ? (
                    <span className="piptv-row__sub">{p.channel_count} {p.channel_count === 1 ? "channel" : "channels"}</span>
                  ) : null}
                  {blocked ? <span className="piptv-row__why">Can't add: {blocked}</span> : null}
                  {clash ? <span className="piptv-row__clash">{clash} — remove it</span> : null}
                </span>
                <span className="piptv-row__price">
                  {rupees(p.price)}
                  <small>{months > 1 ? ` × ${months}` : " / month"}</small>
                </span>
              </label>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AddonPicker;
